import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useAuth, useMySquads, useTenantBootstrap } from '@tark/shared/hooks';
import { Button, Input } from '@tark/shared/components';
import { ArrowRight, Building2, Calendar, Loader2, MessageCircle, RefreshCw, Search, ShieldCheck, Users } from 'lucide-react';
import { cn } from '@tark/shared/lib';
import type { Squad } from '@tark/shared/types';
import { AppPageFrame } from '../components/AppPageFrame';

function formatRelativeTime(value?: string | null): string {
  if (!value) {
    return 'No activity yet';
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return 'No activity yet';
  }

  const diffMinutes = Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

type TenantChatKind = 'coaching' | 'stringer' | 'seller' | 'participant';

function resolveTenantChatKind(tenantArchetype?: string | null, directoryLabel?: string | null): TenantChatKind {
  switch ((tenantArchetype || '').toUpperCase()) {
    case 'SERVICE_PROVIDER':
      return 'stringer';
    case 'COMMERCE':
      return 'seller';
    case 'EVENT_OPERATOR':
      return 'participant';
    default:
      if ((directoryLabel || '').toLowerCase() === 'clients') {
        return 'stringer';
      }
      if ((directoryLabel || '').toLowerCase() === 'customers') {
        return 'seller';
      }
      return 'coaching';
  }
}

function getTenantChatCopy({
  kind,
  tenantName,
  workTabLabel,
}: {
  kind: TenantChatKind;
  tenantName?: string | null;
  workTabLabel: string;
}) {
  switch (kind) {
    case 'stringer':
      return {
        eyebrow: 'Workspace chats',
        description: `Client conversations and internal service threads for ${tenantName || 'this workspace'}.`,
        totalLabel: 'Chats',
        linkedLabel: 'Work Linked',
        workflowTitle: `${tenantName || 'Service'} workflow`,
        workflowDescription: `${workTabLabel} keeps the queue official. These chats handle pickup timing, clarifications, and private client follow-up.`,
        searchPlaceholder: 'Search clients, jobs, or recent messages',
        emptyTitle: 'No client chats yet',
        emptyDescription: 'Client conversations and teammate threads will appear here once requests start moving through the workspace.',
        detailTitle: 'Choose a conversation to work from',
        detailDescription: `Pick a chat from ${tenantName || 'this workspace'} to coordinate service follow-up, teammate handoff, and private client communication.`,
        defaultSubtitle: 'Client conversation ready',
      };
    case 'seller':
      return {
        eyebrow: 'Workspace chats',
        description: `Buyer conversations and internal seller threads for ${tenantName || 'this workspace'}.`,
        totalLabel: 'Chats',
        linkedLabel: 'Work Linked',
        workflowTitle: `${tenantName || 'Seller'} workflow`,
        workflowDescription: `${workTabLabel} keeps the order flow official. These chats handle buyer follow-up, fulfillment questions, and internal coordination.`,
        searchPlaceholder: 'Search customers, orders, or recent messages',
        emptyTitle: 'No customer chats yet',
        emptyDescription: 'Buyer conversations and teammate threads will appear here once order or request work starts flowing through the workspace.',
        detailTitle: 'Choose a conversation to work from',
        detailDescription: `Pick a chat from ${tenantName || 'this workspace'} to handle buyer updates, order questions, and internal seller coordination.`,
        defaultSubtitle: 'Customer conversation ready',
      };
    case 'participant':
      return {
        eyebrow: 'Workspace chats',
        description: `Participant conversations and staff threads for ${tenantName || 'this workspace'}.`,
        totalLabel: 'Chats',
        linkedLabel: 'Event Linked',
        workflowTitle: `${tenantName || 'Event'} workflow`,
        workflowDescription: `${workTabLabel} stays official for event operations. These chats handle participant follow-up and internal staff coordination.`,
        searchPlaceholder: 'Search participants or recent messages',
        emptyTitle: 'No participant chats yet',
        emptyDescription: 'Participant and staff conversations will appear here once the workspace starts coordinating active events.',
        detailTitle: 'Choose a conversation to work from',
        detailDescription: `Pick a chat from ${tenantName || 'this workspace'} to coordinate participants, staff, and event follow-up without crowding the work board.`,
        defaultSubtitle: 'Conversation ready',
      };
    default:
      return {
        eyebrow: 'Organization chats',
        description: `Coach, guardian, and program groups for ${tenantName || 'this organization'}.`,
        totalLabel: 'Groups',
        linkedLabel: 'Program Linked',
        workflowTitle: `${tenantName || 'Organization'} coaching workflow`,
        workflowDescription: `${workTabLabel} stays the source of truth for timing. These chats keep coaches, guardians, and groups aligned around those sessions.`,
        searchPlaceholder: 'Search groups, parents, or recent messages',
        emptyTitle: 'No groups yet',
        emptyDescription: 'Tenant-linked program groups will appear here as soon as coaches and guardians are connected.',
        detailTitle: 'Choose a group to work from',
        detailDescription: `Pick a group from ${tenantName || 'this workspace'} to manage roster, follow-up, and communication around the linked schedule.`,
        defaultSubtitle: 'Group chat ready',
      };
  }
}

export default function SquadsPage() {
  const location = useLocation();
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const {
    data: squads = [],
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useMySquads(Boolean(user));
  const [searchTerm, setSearchTerm] = useState('');

  const isTenantContext = Boolean(activeTenant?.tenant?.id);
  const workTabLabel = activeTenant?.tenant?.workTabLabel || 'Schedule';
  const directoryLabel = activeTenant?.tenant?.directoryTabLabel || 'Members';
  const tenantChatKind = resolveTenantChatKind(activeTenant?.tenant?.tenantArchetype, directoryLabel);
  const tenantChatCopy = getTenantChatCopy({
    kind: tenantChatKind,
    tenantName: activeTenant?.tenant?.displayName,
    workTabLabel,
  });
  const isDetailView = location.pathname !== '/squads';
  const pageTitle = isTenantContext ? 'Chats' : 'Squads';

  const filteredSquads = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return squads;
    }

    return squads.filter((squad) =>
      [squad.name, squad.description, squad.lastMessage, squad.lastMessageSender]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [searchTerm, squads]);

  const summary = useMemo(() => {
    const unreadGroups = squads.filter((squad) => (squad.unreadCount || 0) > 0).length;
    const linkedPrograms = squads.filter((squad) => Boolean(squad.linkedProgramId)).length;

    return {
      total: squads.length,
      unreadGroups,
      linkedPrograms,
    };
  }, [squads]);

  return (
    <AppPageFrame className="flex min-h-[calc(100vh-4rem)] bg-background !pb-0">
      <div
        className={cn(
          'w-full border-r border-border bg-surface md:w-[27rem] xl:w-[30rem] shrink-0',
          isDetailView ? 'hidden md:block' : 'block'
        )}
      >
        <div className="sticky top-0 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="space-y-5 p-5">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-text-subtle">
                    {isTenantContext ? tenantChatCopy.eyebrow : 'Community squads'}
                  </p>
                  <h1 className="mt-2 text-2xl font-semibold tracking-tight text-text">{pageTitle}</h1>
                  <p className="mt-2 text-sm leading-6 text-text-muted">
                    {isTenantContext
                      ? tenantChatCopy.description
                      : 'Casual squads and player conversations tied to your account.'}
                  </p>
                </div>
                {isTenantContext ? (
                  <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                    Org Mode
                  </div>
                ) : null}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <SummaryCard label={isTenantContext ? tenantChatCopy.totalLabel : 'Squads'} value={summary.total} />
                <SummaryCard label="Unread" value={summary.unreadGroups} />
                <SummaryCard label={isTenantContext ? tenantChatCopy.linkedLabel : 'Active'} value={isTenantContext ? summary.linkedPrograms : summary.total} />
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-background p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  {isTenantContext ? <Building2 className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-text">
                      {isTenantContext ? tenantChatCopy.workflowTitle : 'Conversation-first community flow'}
                    </p>
                    <p className="mt-1 text-sm text-text-muted">
                      {isTenantContext
                        ? tenantChatCopy.workflowDescription
                        : 'Squads stay lightweight and familiar, closer to WhatsApp-style coordination for local play.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {isTenantContext ? (
                      <>
                        <NavLink to="/schedule">
                          <Button size="sm" variant="outline" leftIcon={<Calendar className="h-4 w-4" />}>
                            Open {workTabLabel}
                          </Button>
                        </NavLink>
                        <NavLink to="/directory">
                          <Button size="sm" variant="outline" leftIcon={<Users className="h-4 w-4" />}>
                            Open {directoryLabel}
                          </Button>
                        </NavLink>
                      </>
                    ) : (
                      <>
                        <NavLink to="/play">
                          <Button size="sm" variant="outline">
                            Open Play
                          </Button>
                        </NavLink>
                        <NavLink to="/pros">
                          <Button size="sm" variant="outline">
                            Browse PROs
                          </Button>
                        </NavLink>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={isTenantContext ? tenantChatCopy.searchPlaceholder : 'Search squads or recent messages'}
              leftIcon={<Search className="h-4 w-4" />}
            />

            {isLoading && squads.length === 0 ? (
              <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface-elevated/40 px-4 py-5 text-text-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading {pageTitle.toLowerCase()}...</span>
              </div>
            ) : isError && squads.length === 0 ? (
              <SquadsLoadError
                label={isTenantContext ? 'chats' : 'squads'}
                isRetrying={isFetching}
                onRetry={() => void refetch()}
              />
            ) : (
              <>
                {isError ? (
                  <SquadsLoadError
                    compact
                    label={isTenantContext ? 'chats' : 'squads'}
                    isRetrying={isFetching}
                    onRetry={() => void refetch()}
                  />
                ) : null}

                {filteredSquads.length > 0 ? (
                  <section className="space-y-2">
                    {filteredSquads.map((squad) => (
                      <SquadListItem
                        key={squad.id}
                        squad={squad}
                        isTenantContext={isTenantContext}
                        tenantDefaultSubtitle={tenantChatCopy.defaultSubtitle}
                      />
                    ))}
                  </section>
                ) : (
                  <div className="rounded-2xl border border-dashed border-border bg-surface-elevated/40 p-5">
                    <p className="text-base font-semibold text-text">
                      {searchTerm
                        ? isTenantContext
                          ? `No matching ${tenantChatCopy.totalLabel.toLowerCase()}`
                          : 'No matching squads'
                        : isTenantContext
                          ? tenantChatCopy.emptyTitle
                          : 'No squads yet'}
                    </p>
                    <p className="mt-2 text-sm text-text-muted">
                      {searchTerm
                        ? 'Try a broader name, program, or recent message keyword.'
                        : isTenantContext
                          ? tenantChatCopy.emptyDescription
                          : 'Community squads you join will show up here once your account becomes active in group play.'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {isDetailView ? (
          <Outlet />
        ) : (
          <GroupsEmptyState
            isTenantContext={isTenantContext}
            primaryWorkLabel={workTabLabel}
            directoryLabel={directoryLabel}
            tenantTitle={tenantChatCopy.detailTitle}
            tenantDescription={tenantChatCopy.detailDescription}
          />
        )}
      </div>
    </AppPageFrame>
  );
}

function SquadsLoadError({
  compact = false,
  label,
  isRetrying,
  onRetry,
}: {
  compact?: boolean;
  label: string;
  isRetrying: boolean;
  onRetry: () => void;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-border bg-surface-elevated/40',
        compact ? 'flex items-center justify-between gap-3 px-4 py-3' : 'p-5'
      )}
    >
      <div className="min-w-0">
        <p className="font-semibold text-text">{compact ? 'Refresh failed' : `Couldn’t load ${label}`}</p>
        {!compact ? <p className="mt-1 text-sm text-text-muted">Check your connection and try again.</p> : null}
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={isRetrying}
        leftIcon={<RefreshCw className={cn('h-4 w-4', isRetrying && 'animate-spin')} />}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/30 p-4">
      <p className="text-[11px] uppercase tracking-[0.18em] text-text-subtle">{label}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-text">{value}</p>
    </div>
  );
}

function SquadListItem({
  squad,
  isTenantContext,
  tenantDefaultSubtitle,
}: {
  squad: Squad;
  isTenantContext: boolean;
  tenantDefaultSubtitle: string;
}) {
  const privacyLabel = squad.privacyType || squad.privacy || 'PRIVATE';
  const subtitle = squad.lastMessage || squad.description || (isTenantContext ? tenantDefaultSubtitle : 'Open the group to start chatting');

  return (
    <NavLink
      to={`/squads/${squad.id}`}
      className={({ isActive }) =>
        cn(
          'block rounded-2xl border px-4 py-3 transition-all duration-200',
          isActive
            ? 'border-primary/40 bg-primary/10'
            : 'border-border bg-background hover:border-primary/30 hover:bg-surface-elevated'
        )
      }
    >
      {({ isActive }) => (
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-semibold',
              isActive ? 'bg-primary [color:var(--color-on-primary)]' : 'bg-primary/10 text-primary'
            )}
          >
            {(squad.name || 'G').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className={cn('truncate font-semibold', isActive ? 'text-primary' : 'text-text')}>
                {squad.name}
              </p>
              {(squad.unreadCount || 0) > 0 ? (
                <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold [color:var(--color-on-primary)]">
                  {squad.unreadCount}
                </span>
              ) : null}
            </div>

            {squad.lastMessageSender ? (
              <p className="mt-1 truncate text-sm text-text-muted">
                <span className="font-medium text-text-subtle">{squad.lastMessageSender}:</span> {subtitle}
              </p>
            ) : (
              <p className="mt-1 truncate text-sm text-text-muted">{subtitle}</p>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-subtle">
              <span>{squad.memberCount} members</span>
              <span>•</span>
              <span>{privacyLabel}</span>
              {squad.linkedProgramId ? (
                <>
                  <span>•</span>
                  <span>Program linked</span>
                </>
              ) : null}
              <span>•</span>
              <span>{formatRelativeTime(squad.lastMessageAt)}</span>
            </div>
          </div>
        </div>
      )}
    </NavLink>
  );
}

function GroupsEmptyState({
  isTenantContext,
  primaryWorkLabel,
  directoryLabel,
  tenantTitle,
  tenantDescription,
}: {
  isTenantContext: boolean;
  primaryWorkLabel?: string | null;
  directoryLabel?: string | null;
  tenantTitle: string;
  tenantDescription: string;
}) {
  return (
    <div className="hidden h-full items-center justify-center p-8 md:flex">
      <div className="max-w-lg rounded-[2rem] border border-border bg-surface p-8 text-center shadow-sm">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-primary/10">
          {isTenantContext ? (
            <MessageCircle className="h-12 w-12 text-primary" />
          ) : (
            <Users className="h-12 w-12 text-primary" />
          )}
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-text">
          {isTenantContext ? tenantTitle : 'Choose a squad'}
        </h2>
        <p className="mt-3 text-sm leading-6 text-text-muted">
          {isTenantContext
            ? tenantDescription
            : 'Open a squad to review the current shell, see membership, and keep local play coordination simple.'}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {isTenantContext ? (
            <>
              <NavLink to="/schedule">
                <Button leftIcon={<Calendar className="h-4 w-4" />}>Open {primaryWorkLabel || 'Schedule'}</Button>
              </NavLink>
              <NavLink to="/directory">
                <Button variant="outline" leftIcon={<Users className="h-4 w-4" />}>
                  Open {directoryLabel || 'Members'}
                </Button>
              </NavLink>
            </>
          ) : (
            <NavLink to="/play">
              <Button rightIcon={<ArrowRight className="h-4 w-4" />}>Open Play</Button>
            </NavLink>
          )}
        </div>
      </div>
    </div>
  );
}
